import React, { useState, useEffect } from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";
import { useLocation } from "react-router-dom";

function ExamMcqQuestions({
  currentQuestionIndex,
  setCurrentQuestionIndex,
  setAnsweredQuestions,
  setLoading,
  onAttemptTimingLoaded,
  onQuestionsLoaded,
}) {
  const [questions, setQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLocalLoading] = useState(true); 
  const location = useLocation();
  const { assessment_id, attempts } = location.state || {};
  const [startTime, setStartTime] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    setStartTime(new Date().toISOString());
  }, [currentQuestionIndex]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.user_id) {
          setUserId(parsedUser.user_id);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (!assessment_id || !userId) {
      return;
    }

    const fetchQuestions = async () => {
      try {
        setLocalLoading(true); 
        setLoading(true); 
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getQuestionAnswerByTags`,
          {
            params: { assessment_id, user_id: userId, attempt: attempts },
          }
        );

        if (response.data?.success) {
          const fetchedQuestions = response.data.data;
          setQuestions(fetchedQuestions);
          onQuestionsLoaded(fetchedQuestions.length);
          onAttemptTimingLoaded(response.data.timing);
          setStartTime(new Date().toISOString());

          const initialAnswers = {};
          const initialAnsweredQuestions = {};
          fetchedQuestions.forEach((question, index) => {
            const selectedAnswerId = Number(question.choose_answer);
            const hasValidSelectedAnswer =
              question.choose_answer !== null &&
              question.choose_answer !== undefined &&
              question.choose_answer !== "" &&
              question.answers.some(
                (answer) => Number(answer.answer_id) === selectedAnswerId,
              );

            if (hasValidSelectedAnswer) {
              initialAnswers[index] = selectedAnswerId;
              initialAnsweredQuestions[index] = true;
            }
          });
          setSelectedAnswers(initialAnswers);
          setAnsweredQuestions(initialAnsweredQuestions);
        } else {
          setError(response.data.message || "Failed to fetch questions.");
        }
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError("An error occurred while fetching questions.");
      } finally {
        setLocalLoading(false); 
        setLoading(false); 
      }
    };

    fetchQuestions();
  }, [
    assessment_id,
    attempts,
    userId,
    onAttemptTimingLoaded,
    onQuestionsLoaded,
    setAnsweredQuestions,
    setLoading,
  ]);

  const handleAnswerChange = async (question_id, answer_id) => {
    const previousAnswer = selectedAnswers[currentQuestionIndex];

    try {
      setSelectedAnswers((prev) => ({
        ...prev,
        [currentQuestionIndex]: answer_id,
      }));

      if (!userId) {
        throw new Error("User ID not found.");
      }

      await axios.post(`${process.env.REACT_APP_API}/saveAnswer`, {
        answers: { [question_id]: answer_id },
        assessment_id,
        user_id: userId,
        attempts,
        start_time: startTime,
      });

      setAnsweredQuestions((prev) => ({
        ...prev,
        [currentQuestionIndex]: true,
      }));
      setStartTime(new Date().toISOString());
    } catch (error) {
      console.error("Error saving answer:", error);
      setSelectedAnswers((prev) => {
        const restoredAnswers = { ...prev };
        if (previousAnswer === undefined) {
          delete restoredAnswers[currentQuestionIndex];
        } else {
          restoredAnswers[currentQuestionIndex] = previousAnswer;
        }
        return restoredAnswers;
      });
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <p className="mt-3" style={{ fontFamily: "Manrope-Regular" }}>
          Loading Questions...
        </p>
      </div>
    );
  }

  if (error) {
    return <p>{error}</p>;
  }

  if (questions.length === 0) {
    return <p>No questions available.</p>;
  }

  if (currentQuestionIndex < 0 || currentQuestionIndex >= questions.length) {
    return <p>Invalid question index. Please try again.</p>;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswers = currentQuestion?.answers || [];

  return (
    <div
      className="container p-3"
      style={{
        border: "2px solid #faceb1",
        borderRadius: "8px",
        fontFamily: "Manrope-Regular",
        userSelect: "none",
      }}
    >
      <Row className="mb-2">
        <Col xs={8} md={10} className="d-flex justify-content-start">
          <h6 className="mb-4 fw-semibold mobile-questions">
            Q.{currentQuestionIndex + 1} {currentQuestion?.question_text || ""}
          </h6>
        </Col>
        <Col xs={4} md={2} className="d-flex justify-content-end mobile-questions">
          <p className="fw-semibold">{currentQuestion?.points || 1} points</p>
        </Col>
      </Row>

      {currentAnswers.map((answer) => (
        <div
          key={answer.answer_id}
          style={{
            padding: "10px",
            backgroundColor: "#eaeded",
            borderRadius: "4px",
            border: "1px solid #ddd",
            marginBottom: "10px",
            cursor: "pointer",
          }}
          onClick={() =>
            handleAnswerChange(currentQuestion.question_id, answer.answer_id)
          }
        >
          <Form.Check
            type="radio"
            name={`question-${currentQuestionIndex}`}
            label={answer.answer_text}
            checked={selectedAnswers[currentQuestionIndex] === answer.answer_id}
            onChange={() =>
              handleAnswerChange(currentQuestion.question_id, answer.answer_id)
            }
            style={{ pointerEvents: "none" }}
            className="mobile-answers"
          />
        </div>
      ))}
    </div>
  );
}

export default ExamMcqQuestions;
